import React, { useContext } from "react";
import { ReactSVG } from "react-svg";

import arrowIcon from "../../../assets/svg/arrow-s.svg";
import { isViewerInstructorPerson } from "../../../auth/studentProfileAccess";
import { openSubPopup } from "../../Common/popupUIStore";
import { StudentInfoContext } from "./StudentInfoContext";
import {
   fmtReservationDate,
   fmtReservationTime,
   getReservationPrimaryId,
   getReservationRowKey,
   isReservationCancelled,
   isReservationFavorite,
   isReservationImportant,
   readReservationColor,
   readReservationDisplayInstructor,
   readReservationGearbox,
   readReservationSector,
   getReservationLessonKey,
   isCurrentReservation,
} from "./studentInfoShared";

export default function StudentReservationsTab() {
   const {
      loading,
      error,
      sortedReservations,
      setReservationsAscending,
      reservationsAscending,
      reservationNumberByKey,
      fromReservationId,
      currentReservationStart,
      canOpenReservationEdit,
      maskOtherInstructors,
      viewerInstructor,
      viewerUser,
   } = useContext(StudentInfoContext);

   return (
      <>
         {loading && (
            <p className="students-info__loading">Se încarcă programările...</p>
         )}
         {error && <p className="students-info__error">{error}</p>}

         {!loading && !error && (
            <>
               <div className="studentsProfileUI__reservationsMeta">
                  <span className="studentsProfileUI__reservationsCount">
                     {sortedReservations.length} rezervări
                  </span>

                  <button
                     type="button"
                     className="studentsProfileUI__sortBtn"
                     onClick={() => setReservationsAscending((prev) => !prev)}
                     title={
                        reservationsAscending
                           ? "Sortează descrescător"
                           : "Sortează crescător"
                     }
                     aria-label={
                        reservationsAscending
                           ? "Sortează descrescător"
                           : "Sortează crescător"
                     }
                  >
                     <ReactSVG
                        src={arrowIcon}
                        className={
                           "studentsProfileUI__sortIcon" +
                           (reservationsAscending ? "" : " is-desc")
                        }
                     />
                  </button>
               </div>

               {sortedReservations.length === 0 && (
                  <p className="students-info__empty">Nu există programări.</p>
               )}

               {sortedReservations.length > 0 && (
                  <div className="studentsProfileUI__reservationsList">
                     {sortedReservations.map((reservation, index) => {
                        const start =
                           reservation?.startTime ??
                           reservation?.start ??
                           reservation?.startAt ??
                           reservation?.reservation?.startTime ??
                           null;
                        const reservationId = getReservationPrimaryId(reservation);
                        const isCancelled = isReservationCancelled(reservation);
                        const isFavorite = isReservationFavorite(reservation);
                        const isImportant = isReservationImportant(reservation);
                        const cardColor = readReservationColor(reservation);
                        // Afișăm instructorul cu care se face efectiv lecția
                        // (înlocuitorul zilei, dacă există). Un instructor
                        // vede doar propriul nume; restul apar ca „alt
                        // instructor", fără detalii despre înlocuire.
                        const displayInstructor =
                           readReservationDisplayInstructor(reservation);
                        const canShowInstructorName =
                           !maskOtherInstructors ||
                           isViewerInstructorPerson(displayInstructor.instructor, {
                              instructor: viewerInstructor,
                              user: viewerUser,
                           });
                        const instructorName = canShowInstructorName
                           ? displayInstructor.name
                           : "alt instructor";
                        const showSubstituteHint =
                           canShowInstructorName && displayInstructor.isSubstitute;
                        const reservationKey = getReservationLessonKey(reservation);
                        const reservationNumber =
                           reservationNumberByKey.get(reservationKey);
                        const isHighlightedReservation = isCurrentReservation(
                           reservation,
                           fromReservationId,
                           currentReservationStart,
                        );
                        const canOpenReservationCard =
                           canOpenReservationEdit && !!reservationId;

                        return (
                           <article
                              key={getReservationRowKey(reservation, index)}
                              className={
                                 "studentsProfileUI__reservationCard" +
                                 (isHighlightedReservation
                                    ? " studentsProfileUI__reservationCard--current"
                                    : "")
                              }
                              style={{ background: `${cardColor}` }}
                              onClick={() =>
                                 canOpenReservationCard
                                    ? openSubPopup("reservationEdit", {
                                         reservationId,
                                         reservationSnapshot: reservation,
                                      })
                                    : undefined
                              }
                              role={canOpenReservationCard ? "button" : undefined}
                              tabIndex={canOpenReservationCard ? 0 : undefined}
                              onKeyDown={(e) => {
                                 if (!canOpenReservationCard) return;
                                 if (e.key !== "Enter" && e.key !== " ") return;
                                 e.preventDefault();
                                 openSubPopup("reservationEdit", {
                                    reservationId,
                                    reservationSnapshot: reservation,
                                 });
                              }}
                           >
                              {reservationNumber ? (
                                 <span className="studentsProfileUI__reservationNumber">
                                    {reservationNumber}
                                 </span>
                              ) : null}

                              {isCancelled && (
                                 <span className="studentsProfileUI__reservationDot" />
                              )}

                              <p className="studentsProfileUI__reservationDate">
                                 {fmtReservationDate(start)}
                                 {", "}
                                 <span className="studentsProfileUI__reservationTime">
                                    {fmtReservationTime(start)}
                                 </span>
                              </p>
                              <p className="studentsProfileUI__reservationMeta studentsProfileUI__reservationMeta--sub">
                                 Cu {instructorName}
                                 {showSubstituteHint ? " · înlocuitor" : ""}
                              </p>
                              <p className="studentsProfileUI__reservationMeta">
                                 {readReservationSector(reservation)} ·{" "}
                                 {readReservationGearbox(reservation)}
                                 {isFavorite && (
                                    <span className="studentsProfileUI__reservationFlag">
                                       {" "}
                                       ⁂
                                    </span>
                                 )}
                                 {isImportant && (
                                    <span className="studentsProfileUI__reservationFlag">
                                       {" "}
                                       ‼
                                    </span>
                                 )}
                              </p>
                           </article>
                        );
                     })}
                  </div>
               )}
            </>
         )}
      </>
   );
}
